/**
 * CallLogPanel — slide-in drawer showing live call log, AMD queue, in-flight calls.
 * Opened by the ⓘ button in ProgressTracker.
 */

import React from 'react';
import { CallLogEntry } from '../types';
import { CATEGORY_COLORS, CATEGORY_LABELS, PhoneCategory } from '../types';

interface Props {
  onClose: () => void;
  callLog: CallLogEntry[];
  inFlightCalls: Map<string, CallLogEntry>;
  progress: { current: number; total: number; category_counts: Record<string, number> } | null;
}

export const CallLogPanel: React.FC<Props> = ({ onClose, callLog, inFlightCalls, progress }) => {
  const inFlight = Array.from(inFlightCalls.values());
  const remaining = (progress?.total ?? 0) - (progress?.current ?? 0);
  const everQueued = callLog.filter(e => e.ai_queued && e.type === 'call_completed').length;
  const analysisCompleted = callLog.filter(e => e.type === 'analysis_update' && e.analysis_status === 'completed').length;
  const aiQueued = Math.max(0, everQueued - analysisCompleted);

  const fmtTime = (iso: string) => {
    try {
      const d = new Date(iso);
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    } catch { return ''; }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.25)',
          zIndex: 40,
        }}
      />

      {/* Drawer */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0,
        width: 440, maxWidth: '95vw',
        background: 'var(--surface)',
        boxShadow: '-6px 0 24px rgba(0,0,0,0.55)',
        zIndex: 50,
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid var(--border-subtle)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: 'var(--surface-muted)',
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text-strong)' }}>
              Live Call Log
            </h3>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--text-soft)' }}>
              Real-time activity for this job
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 20, color: 'var(--text-faint)', lineHeight: 1, padding: 4,
            }}
          >
            ✕
          </button>
        </div>

        {/* Queue Status */}
        <div style={{
          padding: '12px 20px',
          borderBottom: '1px solid var(--border-subtle)',
          background: 'var(--surface-muted)',
        }}>
          <p style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 700, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Queue Status
          </p>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <QueueChip
              label="AMD Running"
              value={inFlight.length}
              color="var(--status-analysis-processing-fg)" bg="var(--status-analysis-processing-bg)"
              pulse={inFlight.length > 0}
            />
            <QueueChip
              label="Pending AI"
              value={aiQueued}
              color="var(--status-analysis-queued-fg)" bg="var(--status-analysis-queued-bg)"
            />
            <QueueChip
              label="Remaining"
              value={Math.max(0, remaining)}
              color="var(--text)" bg="var(--status-neutral-bg)"
            />
          </div>

          {/* In-flight calls */}
          {inFlight.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <p style={{ margin: '0 0 6px', fontSize: 11, color: 'var(--text-soft)' }}>
                Currently in AMD window:
              </p>
              {inFlight.map(c => (
                <div key={c.call_sid} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '5px 0', fontSize: 12,
                }}>
                  <span style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: 'var(--status-analysis-processing-fg)', display: 'inline-block',
                    animation: 'pulse-dot 1.2s ease-in-out infinite',
                  }} />
                  <span style={{ fontFamily: 'monospace', color: 'var(--text-strong)' }}>{c.phone_number}</span>
                  <span style={{ color: 'var(--text-faint)', fontSize: 11 }}>AMD running…</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Call Log */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
          <p style={{ margin: '8px 20px 6px', fontSize: 11, fontWeight: 700, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Call History ({callLog.length})
          </p>

          {callLog.length === 0 ? (
            <div style={{ padding: '32px 20px', textAlign: 'center', color: 'var(--text-faint)', fontSize: 13 }}>
              No calls logged yet.
            </div>
          ) : (
            callLog.map((entry, i) => (
              <div
                key={i}
                style={{
                  padding: '8px 20px',
                  borderBottom: '1px solid var(--border-subtle)',
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                }}
              >
                {/* Time */}
                <span style={{ fontSize: 11, color: 'var(--text-faint)', whiteSpace: 'nowrap', marginTop: 1, width: 70, flexShrink: 0 }}>
                  {fmtTime(entry.timestamp)}
                </span>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  {entry.type === 'call_started' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--text-strong)' }}>
                        {entry.phone_number}
                      </span>
                      <span style={{
                        fontSize: 11, padding: '2px 7px', borderRadius: 10,
                        background: 'var(--status-analysis-processing-bg)', color: 'var(--status-analysis-processing-fg)', fontWeight: 600,
                      }}>
                        ● Dialing
                      </span>
                    </div>
                  )}

                  {entry.type === 'call_completed' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--text-strong)' }}>
                        {entry.phone_number}
                      </span>
                      <span style={{
                        fontSize: 11, padding: '2px 7px', borderRadius: 10, fontWeight: 600,
                        background: CATEGORY_COLORS[entry.category as PhoneCategory] || 'var(--status-neutral-bg)',
                        color: 'var(--text)',
                      }}>
                        {CATEGORY_LABELS[entry.category as PhoneCategory] || entry.category}
                      </span>
                      {entry.ai_queued && (
                        <span style={{
                          fontSize: 10, padding: '2px 7px', borderRadius: 10,
                          background: 'var(--status-analysis-queued-bg)', color: 'var(--status-analysis-queued-fg)', fontWeight: 600,
                        }}>
                          AI queued
                        </span>
                      )}
                    </div>
                  )}

                  {entry.type === 'analysis_update' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 11, color: 'var(--text-soft)' }}>
                        🤖 AI analysis
                      </span>
                      {entry.category && (
                        <span style={{
                          fontSize: 11, padding: '2px 7px', borderRadius: 10, fontWeight: 600,
                          background: CATEGORY_COLORS[entry.category as PhoneCategory] || 'var(--status-neutral-bg)',
                          color: 'var(--text)',
                        }}>
                          → {CATEGORY_LABELS[entry.category as PhoneCategory] || entry.category}
                        </span>
                      )}
                      <span style={{
                        fontSize: 10, padding: '2px 7px', borderRadius: 10,
                        background: entry.analysis_status === 'completed' ? 'var(--status-success-bg)' : 'var(--status-neutral-bg)',
                        color: entry.analysis_status === 'completed' ? 'var(--status-success-fg)' : 'var(--status-neutral-fg)',
                      }}>
                        {entry.analysis_status}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        <style>{`
          @keyframes pulse-dot {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.4; transform: scale(0.8); }
          }
        `}</style>
      </div>
    </>
  );
};

const QueueChip: React.FC<{
  label: string; value: number; color: string; bg: string; pulse?: boolean;
}> = ({ label, value, color, bg, pulse }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '4px 10px', borderRadius: 20,
    fontSize: 12, fontWeight: 600, background: bg, color,
  }}>
    {pulse && value > 0 && (
      <span style={{
        width: 6, height: 6, borderRadius: '50%', background: color,
        display: 'inline-block',
        animation: 'pulse-dot 1.2s ease-in-out infinite',
      }} />
    )}
    {value} {label}
  </span>
);
